import {isValid} from 'date-fns';
import {BeneficiaryModel} from 'domain/usecases/others/selection-flow/beneficiary-v3';
import {translations} from 'presentation/translations-new';
import {parseDate} from 'presentation/utils';
import * as z from 'zod';

const {
	invalidDate,
	invalidFutureDate,
	invalidCancellationDate,
	invalidPreviousPlanCancellationDate,
} = translations['pt-br'].privateFlow.consultRefund;
const {requiredField} = translations['pt-br'].commons;

const basePatientDataSchema = z.object({
	patient: z.object({
		userCode: z.string().min(1),
		fullName: z.string().min(1),
		fullSocialName: z.string().nullable().optional(),
		beneficiaryType: z.string().min(1),
	}),
	serviceDate: z.string({message: requiredField}),
	specialty: z.object({
		code: z.string().min(1),
		codeCbo: z.string().nullable().optional(),
		description: z.string().min(1),
	}),
	plan: z
		.object({
			line: z.number(),
			planCode: z.number(),
			planName: z.string().min(1),
			detail: z.string().min(1),
			initialEffectiveDate: z.string().min(1),
			finalEffectiveDate: z.string().optional(),
		})
		.optional(),
	planDisabled: z.boolean().optional(),
});

export const patientDataSchema = (
	planBeneficiaries: BeneficiaryModel[],
	isPreviousPlansEnabled = false,
) =>
	basePatientDataSchema.superRefine((data, ctx) => {
		const serviceDate = parseDate(data?.serviceDate);
		const patientUserCode = data.patient?.userCode;
		const shouldValidateServiceDate = !isPreviousPlansEnabled || !!data.plan?.planCode;
		const shouldValidatePlan =
			isPreviousPlansEnabled && !!patientUserCode && !data.planDisabled;
		const dtCancelamento = planBeneficiaries?.find(
			beneficiary => beneficiary.cdUsuarioC === patientUserCode,
		)?.dtCancelamentoC;
		const planLimitDate =
			data.plan?.detail === 'ANTERIOR' && data.plan.finalEffectiveDate
				? parseDate(data.plan.finalEffectiveDate, 'yyyy-MM-dd')
				: undefined;
		const invalidMaxDateMessage = planLimitDate
			? invalidPreviousPlanCancellationDate
			: invalidCancellationDate;
		const maxServiceDate =
			planLimitDate || (dtCancelamento ? parseDate(dtCancelamento) : undefined);

		if (shouldValidatePlan && !data.plan?.planCode) {
			ctx.addIssue({
				path: ['plan'],
				message: requiredField,
				code: z.ZodIssueCode.custom,
			});
			return;
		}

		if (!shouldValidateServiceDate) {
			return;
		}

		if (!isValid(serviceDate)) {
			ctx.addIssue({
				path: ['serviceDate'],
				message: requiredField,
				code: z.ZodIssueCode.custom,
			});
			return;
		}

		const minDate = new Date('2000');
		if (serviceDate && serviceDate <= minDate) {
			ctx.addIssue({
				path: ['serviceDate'],
				message: invalidDate,
				code: z.ZodIssueCode.custom,
			});
			return;
		}

		const isDateAfterPlanLimit = planLimitDate && serviceDate && serviceDate > planLimitDate;
		const isDateAfterCancellationLimit =
			!planLimitDate && maxServiceDate && serviceDate && serviceDate >= maxServiceDate;

		if (isDateAfterPlanLimit || isDateAfterCancellationLimit) {
			ctx.addIssue({
				path: ['serviceDate'],
				message: invalidMaxDateMessage,
				code: z.ZodIssueCode.custom,
			});
		} else if (!maxServiceDate && serviceDate && serviceDate > new Date()) {
			ctx.addIssue({
				path: ['serviceDate'],
				message: invalidFutureDate,
				code: z.ZodIssueCode.custom,
			});
		}
	});

export type PatientDataSchema = z.infer<typeof basePatientDataSchema>;
